// Agent Registration Form — PPRA verification, agency details, region selection
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Alert } from "@/components/shared";
import { api } from "@/services/api";
import { Province, ProvinceLabels, type CreateAgentProfilePayload, type AgentProfile } from "@/types";
import { BadgeCheck, Building2, MapPin } from "lucide-react";

export function AgentRegistrationForm() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  const [formData, setFormData] = useState<CreateAgentProfilePayload>({
    display_name: "",
    agency_name: "",
    ppra_ffc_number: "",
    years_experience: undefined,
    bio: "",
    phone_number: "",
    primary_region: Province.Gauteng,
    subscribed_suburbs: [],
  });

  useEffect(() => {
    async function loadExisting() {
      try {
        const profile = await api.get<AgentProfile>("/agent-profiles/me");
        if (profile) {
          setIsEditMode(true);
          setFormData({
            display_name: profile.display_name,
            agency_name: profile.agency_name,
            ppra_ffc_number: profile.ppra_ffc_number ?? "",
            years_experience: profile.years_experience ?? undefined,
            bio: profile.bio ?? "",
            phone_number: profile.phone_number ?? "",
            primary_region: profile.primary_region as Province,
            subscribed_suburbs: profile.subscribed_suburbs ?? [],
          });
        }
      } catch {
        // No existing profile — stay in create mode
      } finally {
        setIsLoadingProfile(false);
      }
    }
    loadExisting();
  }, []);

  const ffcValid = /^\d{7}$/.test(formData.ppra_ffc_number);

  function updateField<K extends keyof CreateAgentProfilePayload>(field: K, value: CreateAgentProfilePayload[K]) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      // Strip empty optional strings so they aren't sent as ""
      const payload = {
        ...formData,
        bio: formData.bio || undefined,
        phone_number: formData.phone_number || undefined,
      };
      if (isEditMode) {
        const { ppra_ffc_number: _, ...updatePayload } = payload;
        await api.patch("/agent-profiles/me", updatePayload);
      } else {
        await api.post("/agent-profiles", payload);
      }
      navigate("/agent");
    } catch (e: any) {
      const details = e.body?.error?.details;
      if (details?.length) {
        setError(details.map((d: any) => `${d.field}: ${d.issue}`).join("\n"));
      } else {
        setError(e.message || "Failed to save profile");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoadingProfile) {
    return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-sun border-t-transparent rounded-full" /></div>;
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
      <Alert type="info">
        {isEditMode
          ? "Update your agent profile details below. Your FFC number cannot be changed after registration."
          : "Your PPRA FFC number will be verified by our team against the PPRA Agents Register. Once verified, you'll be able to respond to owner requests."}
      </Alert>

      {error && <Alert type="error"><span className="whitespace-pre-line">{error}</span></Alert>}

      {/* Personal & Agency Details */}
      <Card>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Building2 className="h-5 w-5 text-sun" />
          Agency Details
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Display Name</label>
              <input
                type="text"
                required
                value={formData.display_name}
                onChange={(e) => updateField("display_name", e.target.value)}
                placeholder="Nadia Mokoena"
                className="input"
              />
            </div>
            <div>
              <label className="label">Agency Name</label>
              <input
                type="text"
                required
                value={formData.agency_name}
                onChange={(e) => updateField("agency_name", e.target.value)}
                placeholder="Northpoint Realty"
                className="input"
              />
            </div>
          </div>
          <div>
            <label className="label">Years of Experience</label>
            <input
              type="number"
              min={0}
              max={50}
              value={formData.years_experience || ""}
              onChange={(e) => updateField("years_experience", e.target.value ? Number(e.target.value) : undefined)}
              className="input"
            />
          </div>
          <div>
            <label className="label">Bio / Seller Pitch</label>
            <textarea
              value={formData.bio || ""}
              onChange={(e) => updateField("bio", e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="I can provide a suburb valuation, buyer demand snapshot, and a tailored sale plan — without needing your personal details yet."
              className="input resize-none"
            />
          </div>
        </div>
      </Card>

      {/* PPRA Verification */}
      <Card>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <BadgeCheck className="h-5 w-5 text-sun" />
          PPRA Verification
        </h3>
        <div>
          <label className="label">PPRA FFC Number</label>
          <p className="text-xs text-white/40 mb-2">Your Fidelity Fund Certificate number — exactly 7 digits.</p>
          <input
            type="text"
            required
            maxLength={7}
            pattern="\d{7}"
            value={formData.ppra_ffc_number}
            readOnly={isEditMode}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "").slice(0, 7);
              updateField("ppra_ffc_number", v);
            }}
            placeholder="e.g. 1234567"
            className={`input ${isEditMode ? "opacity-50 cursor-not-allowed" : ""} ${formData.ppra_ffc_number && !ffcValid ? "border-red-500" : ""}`}
          />
          {formData.ppra_ffc_number && !ffcValid && (
            <p className="text-xs text-red-400 mt-1">FFC number must be exactly 7 digits</p>
          )}
        </div>
        <div className="mt-4">
          <label className="label">Phone Number (Optional)</label>
          <input
            type="tel"
            value={formData.phone_number || ""}
            onChange={(e) => updateField("phone_number", e.target.value)}
            placeholder="e.g. +27 82 123 4567"
            className="input"
          />
        </div>
      </Card>

      {/* Region Selection */}
      <Card>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <MapPin className="h-5 w-5 text-sun" />
          Primary Region
        </h3>
        <div className="flex gap-3 flex-wrap">
          {Object.values(Province).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => updateField("primary_region", p)}
              className={`px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                formData.primary_region === p
                  ? "border-gold bg-gold/10 text-sun"
                  : "border-white/15 text-white/60 hover:border-white/30"
              }`}
            >
              {ProvinceLabels[p]}
            </button>
          ))}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" loading={isSubmitting} size="lg">
          {isEditMode ? "Update Profile" : "Create Agent Profile"}
        </Button>
      </div>
    </form>
  );
}
